import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import type { ApprovalDecision, PendingApprovalInterrupt } from './types';

interface ApprovalInterruptPanelProps {
  interrupts: PendingApprovalInterrupt[];
  decisions: Record<string, ApprovalDecision | undefined>;
  onDecisionChange: (interruptId: string, decision: ApprovalDecision) => void;
  onSubmit: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  isSubmitting: boolean;
  isCancelling?: boolean;
}

interface ActionCardProps {
  interrupt: PendingApprovalInterrupt;
  actionIndex: number;
  action: {
    name: string;
    arguments: Record<string, unknown>;
    description?: string;
  };
  allowedDecisions: string[];
  decision: ApprovalDecision | undefined;
  onDecisionChange: (decision: ApprovalDecision) => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  allowedDecisions,
  decision,
  onDecisionChange,
}) => {
  const [rejectMessage, setRejectMessage] = useState('');
  const [editArgs, setEditArgs] = useState(() =>
    JSON.stringify(action.arguments, null, 2)
  );

  const canApprove = allowedDecisions.includes('approve');
  const canEdit = allowedDecisions.includes('edit');
  const canReject = allowedDecisions.includes('reject');

  const handleApprove = () => {
    onDecisionChange({ type: 'approve' });
  };

  const handleReject = () => {
    onDecisionChange({
      type: 'reject',
      ...(rejectMessage.trim() ? { message: rejectMessage.trim() } : {}),
    });
  };

  const handleEdit = () => {
    try {
      const parsed = JSON.parse(editArgs) as Record<string, unknown>;
      onDecisionChange({
        type: 'edit',
        editedAction: { name: action.name, args: parsed },
      });
    } catch {
      // Invalid JSON — don't submit
    }
  };

  const isApproved = decision?.type === 'approve';
  const isEdited = decision?.type === 'edit';
  const isRejected = decision?.type === 'reject';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: isApproved
          ? 'success.light'
          : isRejected
            ? 'error.light'
            : isEdited
              ? 'warning.light'
              : (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
        borderRadius: 3,
        bgcolor: isApproved
          ? (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'success.50'
          : isRejected
            ? (theme) => theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'error.50'
            : isEdited
              ? (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.1)' : 'warning.50'
              : 'background.paper',
        boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.05)',
        transition: 'all 0.3s ease',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={action.name} size="small" variant="outlined" />
          {isApproved && (
            <Chip label="Approved" size="small" color="success" />
          )}
          {isEdited && <Chip label="Edited" size="small" color="warning" />}
          {isRejected && <Chip label="Rejected" size="small" color="error" />}
        </Stack>

        {action.description && (
          <Typography variant="body2" color="text.secondary">
            {action.description}
          </Typography>
        )}

        <Box
          sx={{
            bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f7fa',
            borderRadius: 2,
            p: 2,
            fontFamily: "'Fira Code', 'Monaco', monospace",
            fontSize: '0.8rem',
            maxHeight: 250,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            border: '1px solid',
            borderColor: (theme) => theme.palette.mode === 'dark' ? '#333' : '#e0e0e0',
            color: (theme) => theme.palette.mode === 'dark' ? '#e0e0e0' : '#333',
            boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.05)',
          }}
        >
          {JSON.stringify(action.arguments, null, 2)}
        </Box>

        <Stack direction="row" spacing={1}>
          {canApprove && (
            <Button
              size="small"
              variant={isApproved ? 'contained' : 'outlined'}
              color="success"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleApprove}
            >
              Approve
            </Button>
          )}
          {canEdit && (
            <Button
              size="small"
              variant={isEdited ? 'contained' : 'outlined'}
              color="warning"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit & Approve
            </Button>
          )}
          {canReject && (
            <Button
              size="small"
              variant={isRejected ? 'contained' : 'outlined'}
              color="error"
              startIcon={<CancelOutlinedIcon />}
              onClick={handleReject}
            >
              Reject
            </Button>
          )}
        </Stack>

        {canEdit && !isApproved && !isRejected && (
          <TextField
            fullWidth
            multiline
            size="small"
            label="Edit arguments (JSON)"
            value={editArgs}
            onChange={(e) => setEditArgs(e.target.value)}
            slotProps={{
              input: {
                sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
              },
            }}
            minRows={2}
            maxRows={8}
          />
        )}

        {canReject && !isApproved && !isEdited && (
          <TextField
            fullWidth
            size="small"
            label="Rejection reason (optional)"
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
            placeholder="Explain why this action should not proceed..."
          />
        )}
      </Stack>
    </Paper>
  );
};

export const ApprovalInterruptPanel: React.FC<ApprovalInterruptPanelProps> = ({
  interrupts,
  decisions,
  onDecisionChange,
  onSubmit,
  onCancel,
  isSubmitting,
  isCancelling = false,
}) => {
  if (interrupts.length === 0) {
    return null;
  }

  const allDecided = interrupts.every((interrupt) => {
    const actionCount = interrupt.payload.actionRequests.length;
    return actionCount > 0 && decisions[interrupt.interruptId] !== undefined;
  });

  return (
    <Box sx={{ mb: 3 }}>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Tool execution requires your approval before proceeding.
      </Alert>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'warning.light',
          background: (theme) => theme.palette.mode === 'dark' ? `linear-gradient(to bottom right, ${theme.palette.background.paper}, rgba(255, 152, 0, 0.1))` : `linear-gradient(to bottom right, ${theme.palette.background.paper}, rgba(255, 152, 0, 0.05))`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PrivacyTipOutlinedIcon color="warning" />
            <Typography variant="h6" fontWeight={800} sx={{ color: 'warning.main' }}>
              Approval Required
            </Typography>
          </Stack>

          {interrupts.map((interrupt) =>
            interrupt.payload.actionRequests.map((action, actionIndex) => {
              const reviewConfig = interrupt.payload.reviewConfigs.find(
                (config) => config.actionName === action.name
              );
              const allowedDecisions = reviewConfig?.allowedDecisions ?? [
                'approve',
                'reject',
              ];

              return (
                <ActionCard
                  key={`${interrupt.interruptId}-${actionIndex}`}
                  interrupt={interrupt}
                  actionIndex={actionIndex}
                  action={action}
                  allowedDecisions={allowedDecisions}
                  decision={decisions[interrupt.interruptId]}
                  onDecisionChange={(decision) =>
                    onDecisionChange(interrupt.interruptId, decision)
                  }
                />
              );
            })
          )}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            {onCancel && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  void onCancel();
                }}
                disabled={isSubmitting || isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Cancel run'}
              </Button>
            )}

            <Button
              variant="contained"
              onClick={() => {
                void onSubmit();
              }}
              disabled={isSubmitting || isCancelling || !allDecided}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              {isSubmitting ? 'Submitting...' : 'Submit decisions'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ApprovalInterruptPanel;
